import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

import { Logger } from '../../logger/Logger';
import { Curtain } from '../curtain/Curtain';

import type { ErrorBoundaryProps, ErrorBoundaryState } from './types';

/**
 * There's an error boundary component that provides a fallback in case of error in its children components and logs error to Logger.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    } else {
      Logger.logFailure('errorBoundary', {
        place: errorInfo?.componentStack ?? '',
        additional: error.message,
      });
      Logger.logException(error);
    }

    console.error('ErrorBoundary consumed an error:');
    console.error(error.stack ?? error.message);

    if (this.props.critical) {
      Curtain.resurrect();
    }

    this.setState({ hasError: true });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.critical) {
        return Curtain.renderContent(this.props.fallback);
      } else {
        return this.props.fallback;
      }
    }
    return this.props.children;
  }
}
